import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import {
  wsniperIcon, wscannerIcon, wtraderIcon,
  wclaimerIcon, wpanelIcon, wboostIcon, wcheckerIcon,
} from '../assets';
import { whataLabLogo } from '../assets/icons/brand';
import { EXTERNAL_LINKS } from '../config';
import './Hero.css';
import PartnerMarquee from './PartnerMarquee';

const ORBITAL_ICONS = [
  { src: wsniperIcon,  cls: 'orbital-icon-1', label: 'W·Sniper'  },
  { src: wtraderIcon,  cls: 'orbital-icon-2', label: 'W·Trader'  },
  { src: wpanelIcon,   cls: 'orbital-icon-3', label: 'W·Panel'   },
  { src: wscannerIcon, cls: 'orbital-icon-4', label: 'W·Scanner' },
  { src: wclaimerIcon, cls: 'orbital-icon-5', label: 'W·Claimer' },
  { src: wboostIcon,   cls: 'orbital-icon-6', label: 'W·Boost'   },
  { src: wcheckerIcon, cls: 'orbital-icon-7', label: 'W·Checker' },
];

const Hero = () => {
  const { t, language } = useLanguage();
  const isEN = language === 'en';
  const prefersReducedMotion = useReducedMotion();
  
  // Detectar móvil para desactivar animaciones que causan rebote
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Desactivar animaciones en móvil
  const shouldAnimate = !isMobile && !prefersReducedMotion;
  
  return (
    <section className="hero section--hero" id="home">

      {/* ── Orbital background — solo desktop ── */}
      {!isMobile && (
        <div className="hero-orbital-bg" aria-hidden="true">
          <div className="hero-graphic">
            {/* Concentric rings */}
            <div className="graphic-layer graphic-layer-1" />
            <div className="graphic-layer graphic-layer-2" />
            <div className="graphic-layer graphic-layer-3" />

            {/* Center logo */}
            <div className="graphic-center">
              <img src={whataLabLogo} alt="WhataLab" className="graphic-logo" />
            </div>

            {/* Orbiting tool icons */}
            {ORBITAL_ICONS.map(icon => (
              <div key={icon.label} className={`orbital-icon ${icon.cls}`}>
                <img src={icon.src} alt={icon.label} className="tool-icon" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ambient glow blobs — solo desktop */}
      {!isMobile && (
        <>
          <div className="hero-blob hero-blob--tl" />
          <div className="hero-blob hero-blob--br" />
        </>
      )}

      <div className="container hero-container">

        {/* ── Title ── */}
        <motion.h1
          className="hero-title"
          initial={shouldAnimate ? { opacity: 0, y: 10 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          {isEN ? (
            <>
              <span className="hero-title-line1">Trade. Verify.</span>
              <span className="hero-title-line2">Automate.</span>
              <span className="hero-title-line3">All in One Place.</span>
            </>
          ) : (
            <>
              <span className="hero-title-line1">Opera. Verifica.</span>
              <span className="hero-title-line2">Automatiza.</span>
              <span className="hero-title-line3">Todo en Un Lugar.</span>
            </>
          )}
        </motion.h1>

        {/* ── Description ── */}
        <motion.p
          className="hero-desc"
          initial={shouldAnimate ? { opacity: 0, y: 10 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.35 }}
        >
          {isEN
            ? 'Plug your business into Web3. Community-born, enterprise-ready.'
            : 'Conecta tu negocio a Web3. Nacido en comunidad, listo para empresas.'
          }
        </motion.p>

        {/* ── CTAs ── */}
        <motion.div
          className="hero-actions"
          initial={shouldAnimate ? { opacity: 0, y: 10 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.5 }}
        >
          <a href="#faqs" className="hero-btn hero-btn--primary">
            {isEN ? 'Book a Trial' : 'Reservar Demo'}
            <ArrowRight size={16} />
          </a>
          <a
            href={EXTERNAL_LINKS.DISCORD}
            target="_blank"
            rel="noopener noreferrer"
            className="hero-btn hero-btn--secondary"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z"/>
            </svg>
            {isEN ? 'Join Community' : 'Unirse a la Comunidad'}
          </a>
        </motion.div>

        {/* ── Credential marquee ── */}
        <div className="hero-marquee-wrap">
          <PartnerMarquee />
        </div>

        {/* ── Scroll hint ── */}
        <motion.a
          href="#tools"
          className="hero-scroll"
          initial={shouldAnimate ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1 }}
        >
          {isEN ? 'Scroll for more' : 'Desplázate para más'}
          <ChevronDown size={16} className="hero-scroll-icon" />
        </motion.a>

      </div>
    </section>
  );
};

export default Hero;
